/**
 * Toast host — renders the notification stack at the app root and
 * auto-dismisses each item after its ttl. Mounted once in App.tsx.
 *
 * Each toast schedules its own removal via a per-item timer component so
 * the timeout is tied to the toast's lifecycle (and cleared on manual
 * close / unmount) without a central interval.
 */
import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import { dismissToast, type Toast } from "@/state/slices/notificationsSlice";
import styles from "./Toaster.module.css";

function ToastRow({ toast }: { toast: Toast }) {
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (!toast.ttlMs) return;
    const t = window.setTimeout(
      () => dispatch(dismissToast(toast.id)),
      toast.ttlMs,
    );
    return () => window.clearTimeout(t);
  }, [toast.id, toast.ttlMs, dispatch]);

  return (
    <div className={`${styles.toast} ${styles[toast.kind]}`} role="status">
      <div className={styles.bar} />
      <div className={styles.content}>
        <div className={styles.title}>{toast.title}</div>
        {toast.message ? (
          <div className={styles.message}>{toast.message}</div>
        ) : null}
      </div>
      <button
        className={styles.close}
        onClick={() => dispatch(dismissToast(toast.id))}
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  );
}

export function Toaster() {
  const items = useAppSelector((s) => s.notifications.items);
  if (items.length === 0) return null;
  return (
    <div className={styles.host}>
      {items.map((t) => (
        <ToastRow key={t.id} toast={t} />
      ))}
    </div>
  );
}
